// sim/taskQueue.ts — the TASK SCHEDULER: a pooled per-colony task store plus the
// assignment pass and the per-tick pursuit step that drives tasked organisms.
//
// WHAT A "TASK" IS HERE (data-oriented mandate, DESIGN.md §2 / EFFICIENCY.md §B):
//   A task is NOT a heap object. It is an integer id (TaskId) = a row across the
//   parallel typed-array columns below. Total row capacity is
//   `maxColonies * tasksPerColony`; a free-list recycles rows; each colony owns
//   an intrusive singly-linked list of its task ids (head + next) for an O(1)
//   push and a deterministic walk. Mirrors GeneInventory / EntitySystem exactly.
//
// THE SCHEDULER (assignOpenTasks) runs on the AI decision cadence: it walks each
//   colony's open (unassigned, active) tasks and binds each to one eligible IDLE
//   organism of the matching caste, in deterministic activeView() order. The
//   per-organism binding is the O(1) `assignedTask` map (EntityId → TaskId).
//
// THE PURSUIT (runAssignedTask) runs every tick from the organism loop's single
//   hook: a tasked organism steps toward its target (ground or fly, matching its
//   caste) instead of its default caste behaviour, and on arrival performs the
//   task effect and frees the row. Allocation-free; a tiny local stepper is
//   duplicated here on purpose so this module never imports from castes.ts.
//
// DETERMINISM: pure integer state (exact FNV hashing). Lists walk head→next in
//   stable insertion order; ties in assignment break on activeView() order.

import {
  DIR,
  DIR_DX,
  DIR_DY,
  DIR_COUNT,
  NIL,
  KIND,
  CASTE,
  TASK,
  ORGANISM_STATE,
} from '../types.ts';
import type {
  EntityId,
  ColonyId,
  TaskId,
  Caste,
  Tick,
  GeneConfig,
} from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { GenomeSystem } from './genome.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import type { Rng } from '../rng.ts';
import type { ColonySystem } from './colony.ts';
import type { ResourceSystem } from './resources.ts';
import type { Pathing } from '../types.ts';
import type { GeneInventory } from './genes.ts';
import type { EcosystemModifiers } from './ecosystemEvents.ts';
import { chebyshev } from '../engine/Pathing.ts';
import { senseNearestFood } from './sensing.ts';

/**
 * The organism-loop context, widened with the autonomy refs. `OrganismCtx`
 * (castes.ts) is intentionally NOT modified; the colony demo BUILDS this richer
 * ctx and the OrganismSystem reads the optional `tasks`/`onExpand` fields. The
 * base fields below mirror OrganismCtx exactly so an `AutonomyCtx` is assignable
 * wherever an `OrganismCtx` is expected.
 */
export interface AutonomyCtx {
  readonly entities: EntitySystem;
  readonly grid: WorldGrid;
  readonly rng: Rng;
  readonly colonies: ColonySystem;
  readonly resources: ResourceSystem;
  readonly genomes: GenomeSystem;
  /** Ground pathing (the `Pathing` interface; GreedyPathing is the default). */
  readonly pathing: Pathing;
  /**
   * OPTIONAL danger-aware ground pathing for the forage castes' foraging legs
   * (mirrors OrganismCtx.foragePathing). Absent ⇒ foragers use `pathing` (greedy),
   * byte-identical. Task pursuit + soldiers always use `pathing`.
   */
  readonly foragePathing?: Pathing | undefined;
  readonly clock: { tick: Tick };
  readonly genes?: GeneInventory;
  readonly geneConfig?: GeneConfig;
  /** Active ecosystem-event modifiers (optional — events are opt-in; mirrors OrganismCtx). */
  readonly eventMods?: EcosystemModifiers | undefined;
  /**
   * Per-creature movement speed (optional; mirrors OrganismCtx). When true, `advance`
   * (castes.ts) spends each organism's fixed-point `speed` through the sub-tick
   * accumulator. Absent/false ⇒ the original integer stepsPerTick movement,
   * byte-identical. Threaded by the world builders from `config.colony.creatureSpeed`.
   */
  readonly creatureSpeed?: boolean;
  /** The task scheduler (optional — autonomy is opt-in for a world). */
  readonly tasks?: TaskQueue;
  /**
   * Callback the pursuit step fires when an Expand task arrives, so the AI/hive
   * store can found a hive node at (x,y). Optional; paired with `tasks`.
   */
  readonly onExpand?: (colony: ColonyId, x: number, y: number) => void;
  /**
   * Callback the pursuit step fires when a Scout task arrives at (x,y), so the
   * AI can promote that site to an Expand task. Optional; paired with `tasks`.
   */
  readonly onScout?: (colony: ColonyId, x: number, y: number) => void;
  /**
   * Callback the pursuit step fires when a Nurse task arrives at the Nursery, so
   * the hive can accelerate the colony's brood. Optional; paired with `tasks`.
   */
  readonly onNurse?: (colony: ColonyId) => void;
  /**
   * Callback the pursuit step fires when a Haul task arrives at the nest, so the
   * hive can release Storage reserve food into the colony. Optional; with `tasks`.
   */
  readonly onHaul?: (colony: ColonyId) => void;
  /**
   * Callback the pursuit step fires when a Repair task arrives at a chamber, so the
   * hive can restore that chamber's integrity. Optional; paired with `tasks`.
   */
  readonly onRepair?: (colony: ColonyId) => void;
}

export class TaskQueue {
  readonly maxColonies: number;
  readonly tasksPerColony: number;
  readonly capacity: number;

  // --- Task columns (SoA). Index by TaskId. ---
  /** Task kind (TASK.*). */
  private readonly kind: Uint8Array;
  /** Target tile of the task. */
  private readonly targetX: Int32Array;
  private readonly targetY: Int32Array;
  /** Owning colony id. */
  private readonly owner: Int32Array;
  /** Entity currently working this task, or NIL (unassigned/open). */
  private readonly assignedTo: Int32Array;
  /** 1 if the row is a live task, 0 otherwise. */
  private readonly active: Uint8Array;

  // --- Per-colony intrusive list (push-front, deterministic walk). ---
  private readonly head: Int32Array;
  private readonly next: Int32Array;
  private readonly countByColony: Int32Array;

  // --- Free-list allocator (LIFO). ---
  private readonly freeList: Int32Array;
  private freeCount: number;
  private highWater: number;

  /** O(1) EntityId → TaskId map (NIL = no current task). */
  private readonly assignedTask: Int32Array;

  // --- Public counters (deterministic; folded into the hash, read by tests). ---
  enqueuedTotal = 0;
  assignedTotal = 0;
  completedTotal = 0;
  droppedFullTotal = 0;

  constructor(maxColonies: number, entityCapacity: number, tasksPerColony: number) {
    if (maxColonies <= 0) throw new Error('TaskQueue maxColonies must be > 0');
    if (tasksPerColony <= 0) throw new Error('TaskQueue tasksPerColony must be > 0');
    if (entityCapacity <= 0) throw new Error('TaskQueue entityCapacity must be > 0');

    this.maxColonies = maxColonies;
    this.tasksPerColony = tasksPerColony;
    const capacity = maxColonies * tasksPerColony;
    this.capacity = capacity;

    this.kind = new Uint8Array(capacity);
    this.targetX = new Int32Array(capacity);
    this.targetY = new Int32Array(capacity);
    this.owner = new Int32Array(capacity).fill(NIL);
    this.assignedTo = new Int32Array(capacity).fill(NIL);
    this.active = new Uint8Array(capacity);

    this.head = new Int32Array(maxColonies).fill(NIL);
    this.next = new Int32Array(capacity).fill(NIL);
    this.countByColony = new Int32Array(maxColonies);

    this.freeList = new Int32Array(capacity);
    this.freeCount = 0;
    this.highWater = 0;

    this.assignedTask = new Int32Array(entityCapacity).fill(NIL);
  }

  // --- Enqueue / complete ----------------------------------------------------

  /**
   * Enqueue a task of `kind` at (tx,ty) for `owner`. Returns its TaskId, or NIL
   * if the colony is at `tasksPerColony` (or no global row is free). O(1),
   * allocation-free. Push-front onto the owner's intrusive list.
   */
  enqueue(owner: ColonyId, kind: number, tx: number, ty: number): TaskId {
    if ((this.countByColony[owner] as number) >= this.tasksPerColony) {
      this.droppedFullTotal++;
      return NIL;
    }
    let id: TaskId;
    if (this.freeCount > 0) {
      id = this.freeList[--this.freeCount] as number;
    } else if (this.highWater < this.capacity) {
      id = this.highWater++;
    } else {
      this.droppedFullTotal++;
      return NIL;
    }

    this.kind[id] = kind;
    this.targetX[id] = tx;
    this.targetY[id] = ty;
    this.owner[id] = owner;
    this.assignedTo[id] = NIL;
    this.active[id] = 1;

    this.next[id] = this.head[owner] as number;
    this.head[owner] = id;
    this.countByColony[owner] = (this.countByColony[owner] as number) + 1;
    this.enqueuedTotal++;
    return id;
  }

  /**
   * Complete and free task `id`: clears any organism assignment, unlinks the row
   * from its owner's list, and recycles it. Safe on an inactive id (no-op).
   */
  completeTask(id: TaskId): void {
    if (id < 0 || this.active[id] === 0) return;
    const who = this.assignedTo[id] as number;
    if (who !== NIL) this.assignedTask[who] = NIL;

    const owner = this.owner[id] as number;
    // Unlink from the owner's singly-linked list (walk to find the predecessor).
    let cur = this.head[owner] as number;
    if (cur === id) {
      this.head[owner] = this.next[id] as number;
    } else {
      while (cur !== NIL && (this.next[cur] as number) !== id) cur = this.next[cur] as number;
      if (cur !== NIL) this.next[cur] = this.next[id] as number;
    }
    this.next[id] = NIL;
    this.active[id] = 0;
    this.assignedTo[id] = NIL;
    this.owner[id] = NIL;
    this.countByColony[owner] = (this.countByColony[owner] as number) - 1;

    this.freeList[this.freeCount++] = id;
    this.completedTotal++;
  }

  // --- Accessors -------------------------------------------------------------

  taskKindOf(id: TaskId): number {
    return this.kind[id] as number;
  }
  taskTargetXOf(id: TaskId): number {
    return this.targetX[id] as number;
  }
  taskTargetYOf(id: TaskId): number {
    return this.targetY[id] as number;
  }
  taskOwnerOf(id: TaskId): number {
    return this.owner[id] as number;
  }
  taskAssignedToOf(id: TaskId): number {
    return this.assignedTo[id] as number;
  }
  isActive(id: TaskId): boolean {
    return id >= 0 && this.active[id] === 1;
  }
  /** TaskId currently assigned to `entityId`, or NIL. */
  assignedTaskOf(entityId: EntityId): TaskId {
    return this.assignedTask[entityId] as number;
  }
  /** True if `entityId` has a live assigned task. */
  hasTask(entityId: EntityId): boolean {
    return (this.assignedTask[entityId] as number) !== NIL;
  }
  /** Live task count held by `colony`. */
  countOf(colony: ColonyId): number {
    return this.countByColony[colony] as number;
  }

  // --- Per-colony iteration (deterministic insertion order) ------------------

  /** First (most recently enqueued) TaskId of `colony`'s list, or NIL. */
  firstOf(colony: ColonyId): TaskId {
    return this.head[colony] as number;
  }
  /** Next TaskId after `id` in its owner's list, or NIL. */
  nextOf(id: TaskId): TaskId {
    return this.next[id] as number;
  }

  // --- The SCHEDULER pass ----------------------------------------------------

  /**
   * Bind every open (active, unassigned) task to an eligible IDLE organism of
   * the matching caste in that colony. Walks colonies by id, each colony's tasks
   * head→next, and scans entities.activeView() in order for the first eligible,
   * untasked member. Sets the organism's targetX/targetY to the task target so
   * the pursuit step has a destination. Deterministic; O(open tasks * active) —
   * fine at the decision cadence. Allocation-free.
   */
  assignOpenTasks(ctx: AutonomyCtx): void {
    const e = ctx.entities;
    const active = e.activeView();
    const n = e.aliveCount;
    for (let cid = 0; cid < this.maxColonies; cid++) {
      for (let t = this.head[cid] as number; t !== NIL; t = this.next[t] as number) {
        if (this.active[t] === 0) continue;
        if ((this.assignedTo[t] as number) !== NIL) continue;
        const want = wantedCaste(this.kind[t] as number);
        const tx = this.targetX[t] as number;
        const ty = this.targetY[t] as number;
        for (let i = 0; i < n; i++) {
          const id = active[i] as number;
          if ((e.kind[id] as number) !== KIND.Organism) continue;
          if ((e.colony[id] as number) !== cid) continue;
          if ((this.assignedTask[id] as number) !== NIL) continue;
          if (!casteEligible(e.caste[id] as number, this.kind[t] as number)) continue;
          // Bind it.
          this.assignedTo[t] = id;
          this.assignedTask[id] = t;
          e.targetX[id] = tx;
          e.targetY[id] = ty;
          this.assignedTotal++;
          break;
        }
        void want; // (documentary; eligibility already enforced above)
      }
    }
  }

  // --- The PURSUIT step ------------------------------------------------------

  /**
   * Advance the organism's assigned task by one tick: step toward (tx,ty) (fly
   * if it's a Flyer, else ground), and on arrival (chebyshev ≤ 1) perform the
   * task effect and complete the row. Returns true if it HANDLED the organism
   * this tick (the caller then skips the default caste behaviour). Returns false
   * only if the organism has no live task. Allocation-free; deterministic.
   */
  runAssignedTask(ctx: AutonomyCtx, id: EntityId): boolean {
    const task = this.assignedTask[id] as number;
    if (task === NIL || this.active[task] === 0) return false;

    const e = ctx.entities;
    // The unit's resolved genome profile (its captured genetics).
    const cc = ctx.genomes.profile(e.profileRef[id] as number);
    const tx = this.targetX[task] as number;
    const ty = this.targetY[task] as number;
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    e.targetX[id] = tx;
    e.targetY[id] = ty;

    const arrived = chebyshev(ax, ay, tx, ty) <= 1;
    if (!arrived) {
      if ((e.caste[id] as number) === CASTE.Flyer) flyStep(ctx, id, tx, ty);
      else groundStep(ctx, id, tx, ty);
      e.energy[id] = (e.energy[id] as number) - cc.energyDecay;
      return true;
    }

    // On arrival: perform the per-kind effect, then free the row.
    const kind = this.kind[task] as number;
    const owner = this.owner[task] as number;
    if (kind === TASK.Forage) {
      // If food is adjacent, load it into cargo so the normal Worker return leg
      // carries it home next tick.
      if ((e.cargo[id] as number) === 0) {
        const food = senseNearestFood(e, ctx.grid, ax, ay, cc.senseRadius);
        if (food !== NIL && chebyshev(ax, ay, e.posX[food] as number, e.posY[food] as number) <= 1) {
          e.cargo[id] = ctx.resources.consume(food, cc.carryCapacity);
          if ((e.cargo[id] as number) > 0) e.state[id] = ORGANISM_STATE.Returning;
        }
      }
    } else if (kind === TASK.Scout) {
      if (ctx.onScout) ctx.onScout(owner, tx, ty);
    } else if (kind === TASK.Expand) {
      if (ctx.onExpand) ctx.onExpand(owner, tx, ty);
    } else if (kind === TASK.Nurse) {
      if (ctx.onNurse) ctx.onNurse(owner);
    } else if (kind === TASK.Haul) {
      if (ctx.onHaul) ctx.onHaul(owner);
    } else if (kind === TASK.Repair) {
      if (ctx.onRepair) ctx.onRepair(owner);
    }
    // Defend: arrival alone is the effect — the soldier resumes normal striking.

    this.completeTask(task);
    e.energy[id] = (e.energy[id] as number) - cc.energyDecay;
    return true;
  }
}

// ---------------------------------------------------------------------------
// Local, allocation-free movement primitives (duplicated on purpose so this
// module never imports from castes.ts — keeps the caste templates untouched).
// ---------------------------------------------------------------------------

/** One greedy GROUND step toward (tx,ty) via the shared pathing. */
function groundStep(ctx: AutonomyCtx, id: EntityId, tx: number, ty: number): void {
  const e = ctx.entities;
  const ax = e.posX[id] as number;
  const ay = e.posY[id] as number;
  const dir = ctx.pathing.step(ax, ay, tx, ty);
  if (dir !== DIR.Stay) {
    ctx.grid.move(id, ax + (DIR_DX[dir] as number), ay + (DIR_DY[dir] as number));
  }
}

/** One FLYING step toward (tx,ty): bounds-only, ignores walls and the cap. */
function flyStep(ctx: AutonomyCtx, id: EntityId, tx: number, ty: number): void {
  const e = ctx.entities;
  const grid = ctx.grid;
  const ax = e.posX[id] as number;
  const ay = e.posY[id] as number;
  if (ax === tx && ay === ty) return;
  let bestDir: number = DIR.Stay;
  let bestDist = chebyshev(ax, ay, tx, ty);
  for (let d = 0; d < DIR_COUNT; d++) {
    const nx = ax + (DIR_DX[d] as number);
    const ny = ay + (DIR_DY[d] as number);
    if (!grid.inBounds(nx, ny)) continue;
    const dist = chebyshev(nx, ny, tx, ty);
    if (dist < bestDist) {
      bestDist = dist;
      bestDir = d;
    }
  }
  if (bestDir !== DIR.Stay) {
    grid.move(id, ax + (DIR_DX[bestDir] as number), ay + (DIR_DY[bestDir] as number));
  }
}

/** The caste the scheduler wants for a task kind (documentary helper). */
function wantedCaste(kind: number): Caste {
  switch (kind) {
    case TASK.Defend:
      return CASTE.Soldier;
    case TASK.Scout:
      return CASTE.Flyer;
    default:
      return CASTE.Worker; // Forage / Expand / Nurse / Haul / Repair
  }
}

/**
 * Eligibility rule: Forage/Nurse/Haul/Repair→Worker or Specialist (general labor),
 * Defend→Soldier, Scout→Flyer, Expand→Worker. (Specialists share the Worker's
 * gather/labour behaviour, so they can take the hive-tending tasks too.)
 */
function casteEligible(caste: number, kind: number): boolean {
  switch (kind) {
    case TASK.Forage:
    case TASK.Nurse:
    case TASK.Haul:
    case TASK.Repair:
      return caste === CASTE.Worker || caste === CASTE.Specialist;
    case TASK.Defend:
      return caste === CASTE.Soldier;
    case TASK.Scout:
      return caste === CASTE.Flyer;
    case TASK.Expand:
      return caste === CASTE.Worker;
    default:
      return false;
  }
}
